import {applyOperation} from '@github-ui/optimistic-connection'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useQueryClient} from '@github-ui/react-query'
import {useCallback, useMemo} from 'react'

import type {LabelsQueryData} from './types'
import {buildRemoveLabelFromCurrentViewOperation, LABELS_CONNECTION_PATH} from './utils/label-connection-operations'
import {archiveLabelRequest, unarchiveLabelRequest} from './utils/label-requests'

type UseArchiveLabelOptions = {
  labelId: string
  repositoryId: string
  repositoryNameWithOwner: string
  isArchived: boolean
  queryKey?: RouteQueryKey
  setArchiveError: (message: string) => void
  onOptimisticRemove?: () => void
  onRollback?: () => void
}

function getQueryPathname(queryKey: readonly unknown[]): string | undefined {
  const queryDeps = queryKey[4]
  if (!queryDeps || typeof queryDeps !== 'object' || Array.isArray(queryDeps) || !('pathname' in queryDeps)) return
  return typeof queryDeps.pathname === 'string' ? queryDeps.pathname : undefined
}

export function useArchiveLabel({
  labelId,
  repositoryId,
  repositoryNameWithOwner,
  isArchived,
  queryKey,
  setArchiveError,
  onOptimisticRemove,
  onRollback,
}: UseArchiveLabelOptions) {
  const queryClient = useQueryClient()
  const action = isArchived ? 'unarchive' : 'archive'
  const operation = useMemo(() => buildRemoveLabelFromCurrentViewOperation(labelId), [labelId])

  const {mutate, isPending} = useConnectionMutation<void, Error>({
    ownerId: repositoryId,
    connectionPath: LABELS_CONNECTION_PATH,
    mutationFn: () =>
      isArchived
        ? unarchiveLabelRequest(repositoryNameWithOwner, labelId)
        : archiveLabelRequest(repositoryNameWithOwner, labelId),
    onError: error => {
      setArchiveError(error.message)
      onRollback?.()
    },
    onSuccess: async () => {
      if (queryKey) {
        await queryClient.cancelQueries({queryKey})
        queryClient.setQueryData<LabelsQueryData>(queryKey, base =>
          base ? applyOperation(base, repositoryId, operation) : base,
        )
        const pathname = getQueryPathname(queryKey)
        await queryClient.invalidateQueries({
          queryKey: queryKey.slice(0, 4),
          predicate: query => pathname !== undefined && getQueryPathname(query.queryKey) === pathname,
          refetchType: 'inactive',
        })
      }
    },
  })

  const updateArchiveState = useCallback(() => {
    setArchiveError('')
    onOptimisticRemove?.()
    mutate({operation})
  }, [mutate, onOptimisticRemove, operation, setArchiveError])

  return {mutate: updateArchiveState, isPending, action}
}
